import type { FragmentType } from 'src/gql';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { InputErrorMessage } from '@savvy-kit/components/input-error-message';
import { Label } from '@savvy-kit/components/label';
import { Textarea } from '@savvy-kit/components/textarea';
import InputField from '@savvy-kit/patterns/input-field';
import { FormikProvider, useFormik } from 'formik';
import { FormikSelectField } from 'src/components/formik-select-field';
import ItemDialogBackButton from 'src/components/mui/item-dialog-back-button';
import MonetaryInputTextField from 'src/components/mui/monetary-input-text-field';
import TrackedButton from 'src/components/tracked/tracked-button';
import {
  ASSET_CLASS_DISPLAY_NAMES,
  ASSET_CLASS_MANUAL_ADD,
  ASSET_SUBCLASS_DISPLAY_NAMES,
  REAL_ESTATE_SUBCLASSES_MANUAL_ADD,
} from 'src/constants/account-link';
import { UpdateType } from 'src/constants/portfolio';
import { graphql, unmaskFragment } from 'src/gql';
import { formatCurrencyForSubmit } from 'src/utils/currency';
import { removeFalsey } from 'src/utils/object-manipulation';
import { number as yupNumber, object as yupObject, string as yupString } from 'yup';

import type { AssetClassName } from '@savvy/constants/generated/gql-enums';
import type { ID } from '@savvy/utils/id';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

const MAX_STRING_LENGTH = 255;
const MAX_NOTES_LENGTH = 5000;

export const AssetFormFragment = graphql(/* GraphQL */ `
  fragment AssetForm on Asset {
    id
    description
    assetClass
    assetSubclass
    costBasis
    notes
    value {
      amount
    }
  }
`);

const CREATE_ASSET = graphql(/* GraphQL */ `
  mutation CreateAssetFromAssetForm($input: CreateAssetInput!) {
    createAsset(input: $input) {
      asset {
        id
        description
        assetClass
        status
        assetSubclass
        costBasis
        notes
        lastUpdatedAt
        liabilities {
          id
          name
          type
          currentBalance {
            amount
            currencyCode
          }
          originalBalance {
            amount
            currencyCode
          }
          acquiredAt
          timeToMaturity
          timeToMaturityUnit
          annualInterestPercent
          status
          lastUpdatedAt
        }
        value {
          amount
          currencyCode
        }
      }
    }
  }
`);

const UPDATE_ASSET = graphql(/* GraphQL */ `
  mutation UpdateAssetFromAssetForm($input: UpdateAssetInput!) {
    updateAsset(input: $input) {
      asset {
        id
        description
        assetClass
        status
        assetSubclass
        costBasis
        notes
        hiddenFromClient
        hiddenFromNetWorth
        lastUpdatedAt
        companyEquityCompanyName
        companyEquityCompanyTicker
        companyEquityCurrentPrice
        companyEquityGrantType
        companyEquitySharesIssued
        companyEquityVestingStartDate
        companyEquityVestingDuration
        companyEquityVestingCliff
        companyEquityVestingRate
        companyEquityExercisePrice
        liabilities {
          id
          name
          type
          currentBalance {
            amount
            currencyCode
          }
          originalBalance {
            amount
            currencyCode
          }
          providerName
          acquiredAt
          timeToMaturity
          timeToMaturityUnit
          annualInterestPercent
          status
          lastUpdatedAt
        }
        value {
          amount
          currencyCode
        }
      }
    }
  }
`);

function buttonText(isEditMode: boolean, assetClass: AssetClassName) {
  if (assetClass === 'real_estate') {
    if (isEditMode) return 'Update property';
    return 'Add property';
  }
  if (isEditMode) {
    return 'Save asset';
  }
  return 'Add asset';
}

function getSubTypes(assetType: AssetClassName) {
  switch (assetType) {
    case 'real_estate':
      return REAL_ESTATE_SUBCLASSES_MANUAL_ADD;
    case 'other':
    case 'collectible':
    case 'company_equity':
    case 'insurance_policy':
    case 'vehicle':
    default:
      return [];
  }
}

type Props = {
  readonly onClickBack?: () => void;
  readonly clientUserId?: ID;
  readonly onSuccess: Deprecated_HandleItemUpdate;
  readonly hiddenFromClient?: boolean;
  readonly assetFrag?: FragmentType<typeof AssetFormFragment>;
  readonly presetAssetClass?: AssetClassName;
  readonly setAssetId?: (id: number | null) => void;
};

export default function AssetForm({
  onClickBack,
  clientUserId,
  hiddenFromClient,
  onSuccess,
  assetFrag,
  presetAssetClass,
  setAssetId,
}: Props) {
  const asset = unmaskFragment(AssetFormFragment, assetFrag);
  const [createAsset] = useMutationWithErrorLogging(CREATE_ASSET);
  const [updateAsset] = useMutationWithErrorLogging(UPDATE_ASSET);
  const isSmallScreen = useIsSmallScreen();

  const handleUpdateAsset = async () => {
    await updateAsset({
      variables: {
        input: {
          // @ts-expect-error - FIXME: This is using the wrong type
          assetClass: formik.values.assetClass, // FIXME: This graphql field should be AssetClassName instead of string on the backend
          currencyCode: 'USD',
          currentValue: formatCurrencyForSubmit(formik.values.currentValue),
          description: formik.values.description,
          id: asset?.id ?? '',
          notes: formik.values.notes,
          ...removeFalsey({
            assetSubclass: formik.values.assetSubclass, // FIXME: This graphql field should be AssetClassName instead of string on the backend
            hiddenFromClient,
            costBasis: formik.values.costBasis,
          }),
        },
      },
    });
    onSuccess({
      id: asset?.id ?? '',
      type: UpdateType.UPDATE_ASSET,
      item: asset,
    });
  };

  const handleCreateAsset = async () => {
    const res = await createAsset({
      variables: {
        input: {
          // @ts-expect-error - FIXME: This is using the wrong type
          assetClass: formik.values.assetClass, // FIXME: This graphql field should be AssetClassName instead of string on the backend
          currencyCode: 'USD',
          currentValue: formatCurrencyForSubmit(formik.values.currentValue) ?? '',
          description: formik.values.description,
          ...removeFalsey({
            assetSubclass: formik.values.assetSubclass, // FIXME: This graphql field should be AssetClassName instead of string on the backend
            costBasis: formik.values.costBasis,
            userId: clientUserId?.toString(),
            hiddenFromClient,
            notes: formik.values.notes,
          }),
        },
      },
    });
    onSuccess({
      id: res.data?.createAsset?.asset.id.toString() ?? '',
      type: UpdateType.CREATE_ASSET,
      item: res.data?.createAsset?.asset,
    });
    if (setAssetId) {
      setAssetId(Number(res.data?.createAsset?.asset.id));
    }
  };

  const isEditMode = !!asset;
  const isRealEstate = presetAssetClass === 'real_estate' || asset?.assetClass === 'real_estate';
  const formik = useFormik({
    initialValues: {
      description: asset ? asset.description : '',
      currentValue: asset ? asset.value.amount : '',
      costBasis: asset?.costBasis ? asset.costBasis : '',
      assetClass: asset ? asset.assetClass : (presetAssetClass ?? ''),
      assetSubclass: asset?.assetSubclass ? asset.assetSubclass : '',
      notes: asset?.notes ?? '',
      submit: null,
    },
    validationSchema: yupObject({
      description: yupString()
        .max(
          MAX_STRING_LENGTH,
          isRealEstate
            ? `Address must be at most ${MAX_STRING_LENGTH} characters`
            : `Name must be at most ${MAX_STRING_LENGTH} characters`,
        )
        .required(isRealEstate ? 'Address is required' : 'Name is required'),
      assetClass: yupString().required('Asset type is required'),
      currentValue: yupNumber().positive('Enter a number greater than $0').required('Value is required'),
      notes: yupString().max(MAX_NOTES_LENGTH, `Notes must be at most ${MAX_NOTES_LENGTH} characters`),
    }),
    onSubmit: async (): Promise<void> => {
      if (isEditMode) {
        await handleUpdateAsset();
      } else {
        await handleCreateAsset();
      }
    },
  });

  return (
    <FormikProvider value={formik}>
      <form noValidate onSubmit={formik.handleSubmit}>
        {onClickBack && (
          <ItemDialogBackButton
            trackedElementName={isEditMode ? 'update-asset-form-back-button' : 'create-asset-form-back-button'}
            onClick={onClickBack}
          />
        )}
        <InputField
          id="description"
          containerClassName="mb-2"
          autoFocus={!isEditMode}
          error={Boolean(formik.touched.description && formik.errors.description && formik.dirty)}
          errorMessage={formik.errors.description}
          label={isRealEstate ? 'Address' : 'Name'}
          name="description"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          type="text"
          value={formik.values.description}
        />
        {!presetAssetClass && (
          <FormikSelectField
            name="assetClass"
            label="Type"
            className="mt-4"
            options={ASSET_CLASS_MANUAL_ADD.map((key) => ({ value: key, label: ASSET_CLASS_DISPLAY_NAMES[key] }))}
            onValueChange={() => {
              void formik.setFieldValue('assetSubclass', '');
            }}
          />
        )}
        {formik.values.assetClass &&
          // @ts-expect-error - FIXME: This is using the wrong type
          getSubTypes(formik.values.assetClass).length > 0 && (
            <FormikSelectField
              name="assetSubclass"
              label={presetAssetClass ? 'Type' : 'Subtype (optional)'}
              className="mt-4"
              options={getSubTypes(
                // @ts-expect-error - FIXME: This is using the wrong type
                formik.values.assetClass,
              ).map((assetSubclass) => ({
                value: assetSubclass,
                label: ASSET_SUBCLASS_DISPLAY_NAMES[assetSubclass],
              }))}
            />
          )}
        <MonetaryInputTextField
          error={Boolean(formik.touched.currentValue && formik.errors.currentValue)}
          errorMessage={formik.errors.currentValue}
          label="Value"
          name="currentValue"
          formik={formik}
          onBlur={formik.handleBlur}
          value={formik.values.currentValue}
        />
        <div className="mt-4 mb-2">
          <Label htmlFor="notes" className="pb-2">
            Notes
          </Label>
          <Textarea
            id="notes"
            aria-describedby="notes-hint"
            error={Boolean(formik.touched.notes && formik.errors.notes)}
            name="notes"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            placeholder="Account numbers, important dates, or other context"
            rows={3}
            value={formik.values.notes}
          />
          <div className="text-xs pt-1">
            {formik.touched.notes && formik.errors.notes ? (
              <InputErrorMessage id="notes-hint" message={formik.errors.notes} />
            ) : (
              <span id="notes-hint" className="text-secondary">
                Optional
              </span>
            )}
          </div>
        </div>
        {formik.errors.submit && (
          <div className="mt-6">
            <InputErrorMessage message={formik.errors.submit} />
          </div>
        )}
        <div className="flex flex-row justify-end mt-4 mb-6">
          <TrackedButton
            elementName={isEditMode ? 'update-asset-form-save-button' : 'create-asset-form-save-button'}
            disabled={!(isRealEstate && isEditMode) && (formik.isSubmitting || !formik.isValid || !formik.dirty)}
            className={isSmallScreen ? 'w-full' : undefined}
            type="submit"
          >
            {buttonText(
              isEditMode,
              // @ts-expect-error - FIXME: This is using the wrong type
              formik.values.assetClass,
            )}
          </TrackedButton>
        </div>
      </form>
    </FormikProvider>
  );
}
